// RoutingSuggestion -- "who should answer this" in the composer when a thread has
// no owner (Epic C4).
//
// Always a suggestion with a reason, never a silent assignment. routeInbound
// returns { owner_id, owner, reason } | null for the inbound message; the Accept
// button persists a routing RULE (fn_routing_rule_accept) so future similar
// messages route to the same person -- it does NOT assign this thread (spec:
// "never a silent assignment"). To actually assign, the user uses the Assign
// control (<ThreadActions>); this only learns the rule.
//
// Renders only when the thread has no owner (assigned_to is null). It routes the
// thread's LAST message -- the inbound message needing an answer -- so it needs
// the loaded messages. With no messages there is nothing to route (render
// nothing). A null/empty suggestion = none available (render nothing). A thrown
// RPC is a real error -> <ErrorState> with a retry (00_CONTRACT.md §18), but it
// never blocks the composer.
//
// RPC shapes are UNCONFIRMED (docs/CLIENTHUB-RPC-GAPS.md §6) -- routeInbound /
// acceptRoutingRule EXIST (refused a zero-uuid in scripts/probe-conversations.js,
// no mutation). The suggestion is read best-effort against owner_id / member_id /
// term / match_term; a missing term is passed as null and the backend either
// accepts or refuses (refusal surfaces as <ErrorState>, never a silent empty).
import React, { useCallback, useEffect, useState } from "react";
import { routeInbound, acceptRoutingRule, getAssignableMembers } from "../../../api/conversations";
import { ErrorState } from "../../shared/StateMessage";

// Candidate display-name fields on the (UNCONFIRMED) fn_route_inbound shape
// (docs/CLIENTHUB-RPC-GAPS.md §6 — return shape pending a signed-in probe). The
// sibling v_routing_suggestions view exposes the person's name as `goes_to`
// (id as `member_id`), so fn_route_inbound may use either; the declared contract
// names `owner`. Mirrors AssignmentPopover's defensive field-name mapping
// (person_name / full_name / person_id / user_id). One list, used both to detect
// "is a name already present?" (load) and to pick the name to print (render).
const NAME_FIELDS = [
  "owner",
  "owner_name",
  "name",
  "member_name",
  "full_name",
  "person_name",
  "goes_to",
  "assign_to_name",
  "assign_to",
];

/**
 * @param {object} props
 * @param {object} props.thread          { id, assigned_to, assigned, ... } -- the
 *   rail thread. Renders only when `assigned_to` is falsy (no owner).
 * @param {string|null} props.messageId  the inbound message to route (the
 *   thread's last message id). null/empty -> nothing to route, render nothing.
 * @param {Set<string>} [props.existingTerms]
 *   Epic 2 — page-level set of `term`s that already have a routing rule (from
 *   getRoutingRules / ops_routing_rules). When the suggestion's `term` is in
 *   this set, the Accept button is HIDDEN (the rule is already learned) and a
 *   muted note replaces it. Hide, not disable — there is nothing to do.
 * @param {(rule: { owner_id?: string, owner?: string, term?: string|null }) => void} [props.onAccepted]
 *   Notified after a routing rule is accepted. Optional; the component dismisses
 *   itself on success regardless.
 */
export default function RoutingSuggestion({ thread, messageId, existingTerms, onAccepted }) {
  const t = thread || {};
  const hasNoOwner = !t.assigned_to;
  const [status, setStatus] = useState("idle"); // idle|loading|ready|error
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const res = await routeInbound(messageId);
      let sugg = res && typeof res === "object" ? res : null;
      // fn_route_inbound's return shape is UNCONFIRMED. It may return the
      // proposed person's display name directly OR only a member id. Per
      // knowledge-base §2 ("sentences, not spreadsheets") + the DM-name rule
      // (never show a generic noun where a real person is meant), we never
      // print the "someone" fallback when a real person was proposed: if the
      // RPC gave only an id, resolve the name from the team roster
      // (getAssignableMembers — ops_team_members id,name). A roster failure is
      // non-fatal — the suggestion still renders with the best available name
      // (the render fallback chain), never a silent dismissal.
      if (sugg) {
        const mid = sugg.owner_id ?? sugg.member_id ?? null;
        const hasName = NAME_FIELDS.some(
          (f) => sugg[f] != null && String(sugg[f]).trim() !== "",
        );
        if (mid && !hasName) {
          try {
            const rows = await getAssignableMembers();
            const hit = Array.isArray(rows) ? rows.find((r) => r && r.id === mid) : null;
            if (hit && hit.name) sugg = { ...sugg, owner: hit.name };
          } catch {
            // roster unavailable — fall through; render uses the fallback chain
          }
        }
      }
      setSuggestion(sugg);
      setStatus("ready");
    } catch (err) {
      setError(err?.message || "Couldn't load a routing suggestion.");
      setStatus("error");
    }
  }, [messageId]);

  useEffect(() => {
    if (hasNoOwner && messageId) load();
    else {
      setStatus("idle");
      setSuggestion(null);
      setError(null);
    }
  }, [hasNoOwner, messageId, load]);

  const handleAccept = useCallback(async () => {
    if (accepting || accepted) return;
    setAccepting(true);
    setError(null);
    try {
      const term = suggestion?.term ?? suggestion?.match_term ?? null;
      const memberId = suggestion?.owner_id ?? suggestion?.member_id ?? null;
      await acceptRoutingRule({ term, memberId, note: null });
      setAccepted(true);
      onAccepted?.({
        owner_id: suggestion?.owner_id,
        owner: suggestion?.owner,
        term,
      });
    } catch (err) {
      setError(err?.message || "Couldn't save this routing rule.");
    } finally {
      setAccepting(false);
    }
  }, [accepting, accepted, suggestion, onAccepted]);

  if (!hasNoOwner) return null;
  if (!messageId) return null;
  if (accepted) return null;

  if (status === "error") {
    return (
      <div className="mb-2" data-testid="routing-suggestion-error">
        <ErrorState variant="strip" message={error} onRetry={load} />
      </div>
    );
  }
  if (status !== "ready" || !suggestion) return null;

  // Pick the proposed person's name from any of the (UNCONFIRMED) fn_route_inbound
  // name fields. load() already merges a roster-resolved name onto `owner` when the
  // RPC returned only an id, so this finds it here. "someone" is the absolute last
  // resort — a degenerate suggestion with neither a name nor a resolvable id.
  const owner = NAME_FIELDS.map((f) => suggestion[f]).find(
    (n) => n != null && String(n).trim() !== "",
  ) || "someone";
  const reason = suggestion.reason || suggestion.why || null;
  const memberId = suggestion.owner_id ?? suggestion.member_id ?? null;
  // Epic 2 — derive the term the SAME way the accept handler does, then check
  // the page-level rule set. A term already in ops_routing_rules has nothing to
  // accept (the DB function has no unique key, so the UI must prevent the
  // duplicate write). Hide, not disable — a disabled button implies a failed
  // action; there is simply nothing to do.
  const term = suggestion.term ?? suggestion.match_term ?? null;
  const alreadyRuled = term != null && !!existingTerms && existingTerms.has(term);

  return (
    <div
      className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40"
      data-testid="routing-suggestion"
    >
      <div className="flex items-start gap-2">
        <span
          className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-amber-500"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 dark:text-gray-100">
            <span className="font-medium">Who should answer this:</span>{" "}
            <span className="text-[#32B1FF]">{owner}</span>
          </p>
          {reason && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {reason}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {alreadyRuled ? (
              <span
                className="text-xs text-gray-400 dark:text-gray-500"
                data-testid="routing-suggestion-ruled"
              >
                routing rule already learned for this term
              </span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting || !memberId}
                  className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
                  data-testid="routing-suggestion-accept"
                >
                  {accepting ? "Saving…" : "Accept routing rule"}
                </button>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Applies to future messages like this — not an assignment.
                </span>
              </>
            )}
          </div>
          {error && (
            <div className="mt-2">
              <ErrorState variant="strip" message={error} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
